import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { apiLogin, getPortalEntryPath, isAdminUser } from '../api/client';
import PortalAuthShell from './PortalAuthShell';

interface LoginResponse {
  success: boolean;
  status?: number;
  message?: string;
  error?: string;
  errors?: string[];
  user?: {
    role?: string;
  };
}

export default function LoginPage() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState((location.state as { message?: string } | null)?.message || '');
  const navigate = useNavigate();

  useEffect(() => {
    const nextPath = getPortalEntryPath();

    if (nextPath !== '/member/login') {
      navigate(nextPath, { replace: true });
    }
  }, [navigate]);

  const getLoginErrorMessage = (response: LoginResponse) => {
    if (typeof response.message === 'string' && response.message.trim()) {
      return response.message.trim();
    }

    if (typeof response.error === 'string' && response.error.trim()) {
      return response.error.trim();
    }

    if (Array.isArray(response.errors) && response.errors.length > 0) {
      return response.errors.join(', ');
    }

    return 'Login failed';
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    setNotice('');

    const res: LoginResponse = await apiLogin(email, password, rememberMe);

    if (res.success) {
      if (isAdminUser(res.user)) {
        navigate('/admin', { replace: true });
        return;
      }

      navigate('/member/articles', { replace: true });
    } else {
      setError(getLoginErrorMessage(res));
    }
  };

  return (
    <PortalAuthShell
      title="Welcome"
      subtitle="Sign in to continue to your portal."
    >
      <Stack as="form" spacing={5} onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <Heading size="lg">Login</Heading>
          <Text color="gray.600">Enter your email and password to continue.</Text>
        </Stack>

        {error ? (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            {error}
          </Alert>
        ) : null}

        {notice ? (
          <Alert status="success" borderRadius="lg">
            <AlertIcon />
            {notice}
          </Alert>
        ) : null}


        <FormControl isRequired>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            size="lg"
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Password</FormLabel>
          <Input
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            size="lg"
          />
        </FormControl>

        <FormControl display="flex" alignItems="center">
          <input
            id="rememberMe"
            type="checkbox"
            checked={rememberMe}
            onChange={e => setRememberMe(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          <FormLabel htmlFor="rememberMe" mb="0" fontWeight="normal">
            Remember me
          </FormLabel>
        </FormControl>

        <Text mt={-2} textAlign="right">
          <Link as={RouterLink} to="/member/forgot-password" color="blue.600" fontWeight="600">
            Forgot password?
          </Link>
        </Text>

        <Button type="submit" colorScheme="blue" size="lg">
          Login
        </Button>

        <Text color="gray.600">
          Don&apos;t have an account?{' '}
          <Link as={RouterLink} to="/member/register" color="blue.600" fontWeight="600">
            Register
          </Link>
        </Text>
      </Stack>
    </PortalAuthShell>
  );
}
